import { Color, Spacing } from '@persona-id/design-tokens';
import { MergeIcon, PlusIcon, WorkflowsIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { cloneDeep, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import {
  Column,
  FieldSchemaEditorSupportedTypes,
  FieldSchemaEditorTable,
  FieldSchemaPanel,
  FocusReason,
  getAllFieldKeys,
  getAllFields,
  getAllRequirePresenceConfigs,
  replaceNestedField,
  validateAllSchemas,
  validateSingleFieldSchema,
} from 'dashboard/components-lib/FieldSchemaEditor';
import {
  DEFAULT_FIELD_SCHEMA,
  EditorableSchema,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { FieldSchemaEditorForm } from 'dashboard/components-lib/FieldSchemaEditorForm';
import { FieldSchemaEditorFormValues } from 'dashboard/components-lib/FieldSchemaEditorForm/FieldSchemaEditorForm';
import ComputedFieldControls from 'dashboard/components-lib/FieldSchemaEditorForm/panel-additional/ComputedFieldControls';
import RequirePresenceControls from 'dashboard/components-lib/FieldSchemaEditorForm/panel-additional/RequirePresenceControls';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import {
  Backdrop,
  FULL_SCREEN_HEADER_HEIGHT,
  MainColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TransactionType, TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { Trigger } from 'dashboard/models/trigger';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import DashboardTransactionTypesActiveFieldsHeader from 'dashboard/views/DashboardTransactionTypesActiveFieldsView/DashboardTransactionTypesActiveFieldsHeader';
import OverflowMenu from 'dashboard/views/DashboardTransactionTypesActiveFieldsView/OverflowMenu';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { FieldSchema, isComputedField } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const RIGHT_COLUMN_PORTAL_ID = 'txn-fields-portal';
const MAX_RECOMMENDED_NESTING_DEPTH = 8;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
`;

const StyledMainColumn = styled(MainColumn)`
  flex: 1 0 710px;
  overflow: auto;
`;

const StyledBackdrop = styled(Backdrop)`
  padding: ${Spacing[7]};
`;

interface DashboardTransactionTypesActiveFieldsViewProps {
  transactionType: TransactionType;
  fieldKeyToWorkflowWithRelationships: Record<
    string,
    { workflow: Workflow; workflowVersion: WorkflowVersion; trigger: Trigger }
  >;
  handleDeactivateTransactionType: () => void;
  handleActivateTransactionType: () => void;
  handleDeactivateField: (fieldSchemaKey: string) => void;
  handleUpdateTransactionType: (
    transactionType: TransactionType,
    deactivateFieldSchemaKeys: string[],
  ) => void;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => void;
  isUpdatingTransactionType: boolean;
}

const DashboardTransactionTypesActiveFieldsView = ({
  transactionType,
  fieldKeyToWorkflowWithRelationships,
  handleDeactivateTransactionType,
  handleActivateTransactionType,
  handleDeactivateField,
  handleUpdateTransactionType,
  showMessage,
  isUpdatingTransactionType,
}: DashboardTransactionTypesActiveFieldsViewProps) => {
  const isComputedFieldsEnabled = useFeatureFlag(
    'dashboard:enable-workflows-computed-field-triggers',
  );
  const isRequirePresenceEnabled = useFeatureFlag('dashboard:txn-additional-field-validations');
  const navigate = useNavigate();
  const status = transactionType?.attributes['status'];
  const [selectedFieldSchemaPath, setSelectedFieldSchemaPath] = useState<string>('');
  const [selectedFieldSchemaReason, setSelectedFieldSchemaReason] = useState<FocusReason>('add');
  const [inactiveFieldKey, setInactiveFieldKey] = useState<string | null>(null);
  const [
    showDeactivateTransactionTypeModal,
    { enable: openDeactivateTransactionTypeModal, disable: closeDeactivateTransactionTypeModal },
  ] = useToggle(false);

  const hasWritePermission = usePermission(Permission.TransactionTypeWrite);

  const [name, setName] = useState<string>(transactionType?.attributes?.['name']);

  // Candidate for saving to transaction. Should be valid
  const [activeFieldSchemas, setActiveFieldSchemas] = useState<FieldSchemaEditorSupportedTypes[]>([
    ...(transactionType?.attributes?.['field-schemas'].filter(
      (type) => type.config['deactivated-at'] == null,
    ) || []),
  ]);

  // Things users are actively typing into. Could be invalid
  const [draftFieldSchemas, setDraftFieldSchemas] = useState<FieldSchemaEditorSupportedTypes[]>([]);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [erroredKeys, setErroredKeys] = useState<string[]>([]);

  const refreshActiveSchema = useCallback((newSchemas: FieldSchemaEditorSupportedTypes[]) => {
    setActiveFieldSchemas(newSchemas);
    setDraftFieldSchemas(cloneDeep(newSchemas));
    setErroredKeys([]);
  }, []);

  useEffect(() => {
    refreshActiveSchema([
      ...(transactionType?.attributes?.['field-schemas'].filter(
        (type) => type.config['deactivated-at'] == null,
      ) || []),
    ]);
  }, [refreshActiveSchema, transactionType]);

  const inactiveFieldSchemas = useMemo(
    () =>
      transactionType?.attributes?.['field-schemas'].filter(
        (type) => type.config['deactivated-at'],
      ),
    [transactionType],
  );

  const preexistingFieldKeys = useMemo(
    () => getAllFieldKeys(transactionType.attributes['field-schemas']),
    [transactionType],
  );

  const selectedFieldSchema = useMemo(() => {
    if (!selectedFieldSchemaPath) {
      return null;
    }
    return getAllFields(draftFieldSchemas).find(({ key }) => key === selectedFieldSchemaPath)
      ?.fieldSchema;
  }, [draftFieldSchemas, selectedFieldSchemaPath]);

  const deleteFromActiveFieldSchema = (key: string) => {
    if (preexistingFieldKeys.includes(key)) {
      return;
    }
    setSelectedFieldSchemaPath('');
    const { newSchemas } = replaceNestedField(draftFieldSchemas, key, null);
    refreshActiveSchema(newSchemas);

    showMessage('Deleted 1 field', MessageType.Success, false);
  };

  const saveTransactionType = () => {
    const erroredKeys = validateAllSchemas(activeFieldSchemas);
    if (erroredKeys.length > 0) {
      setErroredKeys(erroredKeys);
      showMessage('Please fix input errors before saving', MessageType.Failure, false);
      return;
    }
    handleUpdateTransactionType(
      {
        ...transactionType,
        attributes: {
          ...transactionType.attributes,
          name: name,
          'field-schemas': activeFieldSchemas.concat(inactiveFieldSchemas) as FieldSchema[],
        },
      },
      [],
    );
  };

  const saveTransactionTypeFromForm = (formValues: FieldSchemaEditorFormValues) => {
    handleUpdateTransactionType(
      produce(transactionType, (draftType) => {
        draftType.attributes.name = name;
        draftType.attributes['field-schemas'] = formValues.fieldSchemas as FieldSchema[];
      }),
      formValues?.deactivateFieldSchemaKeys ?? [],
    );
  };

  const deactivateTransactionTypeField = () => {
    const fieldKey = inactiveFieldKey || selectedFieldSchema?.key;
    setSelectedFieldSchemaPath('');
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleDeactivateField(fieldKey);
  };

  const fieldSchemasAreEqual = useMemo(() => {
    if (transactionType.attributes['name'] !== name) {
      return false;
    }

    const preexistingFieldSchemas = transactionType.attributes['field-schemas'].filter(
      (type) => type.config['deactivated-at'] == null,
    );

    // We are making the assumption that currentKeys is a strict superset of preexistingActiveKeys
    const currentKeys = getAllFieldKeys(activeFieldSchemas);
    const preexistingActiveKeys = getAllFieldKeys(preexistingFieldSchemas);

    if (currentKeys.length !== preexistingActiveKeys.length) {
      return false;
    }

    const currentRequirePresenceConfigs = getAllRequirePresenceConfigs(activeFieldSchemas);
    const preexistingRequirePresenceConfigs = getAllRequirePresenceConfigs(preexistingFieldSchemas);
    const requirePresenceConfigAreEqual = currentRequirePresenceConfigs.every(
      (requirePresenceConfig, idx) =>
        requirePresenceConfig === preexistingRequirePresenceConfigs[idx],
    );

    if (!requirePresenceConfigAreEqual) {
      return false;
    }

    return true;
  }, [name, activeFieldSchemas, transactionType]);

  const hasUnsavedDrafts = useMemo(() => {
    return !isEqual(activeFieldSchemas, draftFieldSchemas);
  }, [activeFieldSchemas, draftFieldSchemas]);

  const tableIsEditable = useMemo(() => {
    return status === TransactionTypeStatus.Active && hasWritePermission && !hasUnsavedDrafts;
  }, [hasUnsavedDrafts, hasWritePermission, status]);

  const customColumns: Column[] = isComputedFieldsEnabled
    ? [
        {
          header: '' /* computed field */,
          generateContent: ({ field, prefix }) => {
            if (!isComputedField(field as FieldSchema)) {
              return <></>;
            }

            const workflowId = fieldKeyToWorkflowWithRelationships[field.key]?.workflow.id;
            const workflowVersionId =
              fieldKeyToWorkflowWithRelationships[field.key]?.workflowVersion.id;
            return (
              <WorkflowsIcon
                id={`${prefix}-edit-workflow-button`}
                role='button'
                aria-label={`Edit workflow for computed field ${prefix}`}
                width='16px'
                onClick={() => {
                  navigate(`/dashboard/workflows/${workflowId}/${workflowVersionId}`);
                }}
              />
            );
          },
        },
      ]
    : [];
  customColumns.push({
    header: '' /* overflow actions */,
    width: '60px',
    generateContent: (props) => {
      return (
        tableIsEditable && (
          <div style={{ marginLeft: 'auto' }}>
            <OverflowMenu
              draftFieldKeys={[]}
              fieldSchemas={transactionType.attributes['field-schemas']}
              setInactiveFieldKey={setInactiveFieldKey}
              deleteFromActiveFieldSchema={deleteFromActiveFieldSchema}
              {...props}
            />
          </div>
        )
      );
    },
  });

  const enableFieldSchemaEditorForm = useFeatureFlag('dashboard:enable-field-schema-editor-form');

  return (
    <>
      <ModalWrapper isOpen={!!inactiveFieldKey} handleCloseModal={() => setInactiveFieldKey(null)}>
        <ConfirmationModal
          cancelAction={() => setInactiveFieldKey(null)}
          confirmAction={() => deactivateTransactionTypeField()}
          header={`Are you sure you want to deactivate field ${inactiveFieldKey}?`}
          confirmActionText='Deactivate Field'
        >
          Deactivating a field that is used in Workflows or elsewhere within Persona may cause
          errors.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper
        isOpen={showDeactivateTransactionTypeModal}
        handleCloseModal={closeDeactivateTransactionTypeModal}
      >
        <ConfirmationModal
          cancelAction={closeDeactivateTransactionTypeModal}
          confirmAction={handleDeactivateTransactionType}
          header='Deactivate this transaction type?'
          confirmActionText='Deactivate'
        >
          Deactivating a transaction type that is used in Workflows or elsewhere within Persona may
          cause errors.
        </ConfirmationModal>
      </ModalWrapper>

      <FeatureFlagged featureFlag='dashboard:txn-layout-editor' fallback={null} invert>
        <DashboardTransactionTypesActiveFieldsHeader
          name={name}
          transactionId={transactionType.id}
          status={status}
          disabled={fieldSchemasAreEqual || selectedFieldSchemaPath !== ''}
          showSaveButton={!enableFieldSchemaEditorForm}
          unsavedChanges={!fieldSchemasAreEqual}
          setName={setName}
          saveTransactionType={saveTransactionType}
          openDeactivateTransactionTypeModal={openDeactivateTransactionTypeModal}
          handleActivateTransactionType={handleActivateTransactionType}
        />
      </FeatureFlagged>
      <Container>
        <StyledMainColumn>
          <StyledBackdrop>
            <NavConfirmPrompt prevent={!fieldSchemasAreEqual} />
            {enableFieldSchemaEditorForm ? (
              <FieldSchemaEditorForm
                fieldSchemas={transactionType?.attributes?.['field-schemas'] || []}
                isPending={isUpdatingTransactionType}
                canDeactivate={(fieldSchema, isNested) => {
                  // Do not allow deactivate on nested schema
                  return !isNested && !!fieldSchema.persisted;
                }}
                canDelete={(fieldSchema, _) => {
                  return !fieldSchema.persisted;
                }}
                canEdit={(fieldSchema) => !fieldSchema.persisted}
                nullState={
                  <Permissioned
                    permission={Permission.TransactionTypeView}
                    fallback={
                      <div>
                        User permission required to view transaction type details. Please contact
                        your account admin if you&#39;d like to view transaction types.
                      </div>
                    }
                  >
                    <div>This transaction currently has no fields configured.</div>
                    <Spacer height={2} />
                    <div>
                      <Link
                        target='_blank'
                        rel='noreferrer'
                        to='https://docs.withpersona.com/docs/transactions-overview'
                      >
                        Learn more here
                      </Link>
                    </div>
                  </Permissioned>
                }
                additionalColumns={
                  isComputedFieldsEnabled
                    ? [
                        {
                          header: '' /* computed field */,
                          generateContent: ({ field, prefix }) => {
                            if (!isComputedField(field as FieldSchema)) {
                              return <></>;
                            }

                            const workflowId =
                              fieldKeyToWorkflowWithRelationships[field.key]?.workflow.id;
                            const workflowVersionId =
                              fieldKeyToWorkflowWithRelationships[field.key]?.workflowVersion.id;
                            return (
                              <WorkflowsIcon
                                id={`${prefix}-edit-workflow-button`}
                                role='button'
                                aria-label={`Edit workflow for computed field ${prefix}`}
                                width='16px'
                                onClick={() => {
                                  navigate(
                                    `/dashboard/workflows/${workflowId}/${workflowVersionId}`,
                                  );
                                }}
                              />
                            );
                          },
                        },
                      ]
                    : []
                }
                rightPanelPortalId={RIGHT_COLUMN_PORTAL_ID}
                editable={tableIsEditable}
                onSubmit={(formValues) => saveTransactionTypeFromForm(formValues)}
                renderAdditionalPanelContent={(
                  fieldSchema: EditorableSchema,
                  fieldName: string,
                  formContext: ReturnType<typeof useFormContext>,
                ) => (
                  <>
                    <ComputedFieldControls
                      fieldSchema={fieldSchema}
                      fieldName={fieldName}
                      formContext={formContext}
                      canEdit={(fieldSchema) => !fieldSchema.persisted}
                    />
                    <RequirePresenceControls
                      fieldSchema={fieldSchema}
                      fieldName={fieldName}
                      formContext={formContext}
                      canEdit={(fieldSchema) => !fieldSchema.persisted}
                    />
                  </>
                )}
                showActive
                showInactive={false}
              />
            ) : (
              <Card>
                <Card.Body>
                  <Card.Title
                    actions={
                      <Permissioned permission={Permission.TransactionTypeWrite}>
                        <Button
                          aria-label='Add new field'
                          id='transaction-types-show__create-field-button'
                          variant='basic'
                          size='md'
                          disabled={!tableIsEditable}
                          prefixIcon={<PlusIcon />}
                          onClick={() => {
                            setDraftFieldSchemas([...draftFieldSchemas, DEFAULT_FIELD_SCHEMA]);
                            setSelectedFieldSchemaPath(`${draftFieldSchemas.length}`);
                          }}
                        >
                          Add Field
                        </Button>
                      </Permissioned>
                    }
                  >
                    Fields
                  </Card.Title>
                  <FieldSchemaEditorTable
                    productIcon={<MergeIcon size={48} color={Color.BorderIconSecondary} />}
                    inputSchema={draftFieldSchemas}
                    customColumns={customColumns}
                    focusedKey={selectedFieldSchemaPath}
                    erroredKeys={erroredKeys}
                    uneditableKeys={preexistingFieldKeys}
                    editable={tableIsEditable}
                    nullState={
                      <Permissioned
                        permission={Permission.TransactionTypeView}
                        fallback={
                          <div>
                            User permission required to view transaction type details. Please
                            contact your account admin if you&#39;d like to view transaction types.
                          </div>
                        }
                      >
                        <div>This transaction currently has no fields configured.</div>
                        <Spacer height={2} />
                        <div>
                          <Link
                            target='_blank'
                            rel='noreferrer'
                            to='https://docs.withpersona.com/docs/transactions-overview'
                          >
                            Learn more here
                          </Link>
                        </div>
                      </Permissioned>
                    }
                    onUpdateSchema={(
                      fieldSchemas: FieldSchemaEditorSupportedTypes[],
                      autoValidate?: boolean,
                    ) => {
                      setDraftFieldSchemas(fieldSchemas);
                      if (autoValidate) {
                        const erroredKeys = validateAllSchemas(
                          fieldSchemas.concat(inactiveFieldSchemas),
                        );
                        if (erroredKeys.length === 0) {
                          refreshActiveSchema(fieldSchemas);
                        } else {
                          setErroredKeys(erroredKeys);
                          setSelectedFieldSchemaPath(erroredKeys[0]);
                        }
                      }
                    }}
                    onFocusSchema={(path: string, focusReason: FocusReason) => {
                      // Do not allow user to navigate away until they finish editing the current one
                      if (!hasUnsavedDrafts) {
                        setSelectedFieldSchemaPath(path);
                        setSelectedFieldSchemaReason(focusReason);
                      }
                    }}
                  />
                </Card.Body>
              </Card>
            )}
          </StyledBackdrop>
        </StyledMainColumn>
        {selectedFieldSchema != null && (
          <FieldSchemaPanel
            isComputedFieldsEnabled={isComputedFieldsEnabled}
            isRequirePresenceEnabled={isRequirePresenceEnabled}
            fieldSchema={selectedFieldSchema || DEFAULT_FIELD_SCHEMA}
            isNewField={
              status === TransactionTypeStatus.Active &&
              !preexistingFieldKeys.includes(selectedFieldSchemaPath)
            }
            errorMessage={errorMessage}
            selectReason={selectedFieldSchemaReason}
            allowHash={selectedFieldSchemaPath.split('.').length < MAX_RECOMMENDED_NESTING_DEPTH}
            onEditing={(fieldSchema) => {
              const { newSchemas, newPath } = replaceNestedField(
                draftFieldSchemas,
                selectedFieldSchemaPath,
                fieldSchema,
              );
              const { message, validForDraft } = validateSingleFieldSchema(
                fieldSchema,
                newSchemas.concat(inactiveFieldSchemas),
              );
              setErrorMessage(message);
              if (validForDraft) {
                setDraftFieldSchemas(newSchemas);
                setSelectedFieldSchemaPath(newPath);
              }
            }}
            onCancel={() => {
              setDraftFieldSchemas(cloneDeep(activeFieldSchemas));
              setSelectedFieldSchemaPath('');
              setErrorMessage('');
              setErroredKeys([]);
            }}
            onUpdate={(fieldSchema) => {
              const { newSchemas } = replaceNestedField(
                draftFieldSchemas,
                selectedFieldSchemaPath,
                fieldSchema,
              );
              const { message } = validateSingleFieldSchema(
                fieldSchema,
                newSchemas.concat(inactiveFieldSchemas),
              );
              setErrorMessage(message);
              if (!message) {
                refreshActiveSchema(newSchemas);
                setSelectedFieldSchemaPath('');
              }
            }}
          />
        )}
        <div id={RIGHT_COLUMN_PORTAL_ID} />
      </Container>
    </>
  );
};

export default DashboardTransactionTypesActiveFieldsView;
